import { createContext, useContext, useEffect, useState } from 'react';

import type { User } from 'src/shared/graphql/generated';
import type { WrappedSplitClient } from 'src/shared/hooks/useSplit/types';

type SplitContext = {
  attributes: SplitIO.Attributes;
  client: Promise<SplitIO.IClient | undefined> | null;
  updateAttributes: (user?: User | UserWithAdditionalAttributes) => void;
  updateUserId: (id: string) => void;
};

export type UserWithAdditionalAttributes = User & {
  isInsurance?: boolean;
};

const defaultValue: SplitContext = {
  attributes: {},
  client: null,
  updateAttributes: () => {},
  updateUserId: () => {},
};

const context = createContext(defaultValue);
const { Provider } = context;
const useSplit = () => {
  const {
    attributes,
    client: splitClient,
    updateUserId,
    updateAttributes,
  } = useContext(context);

  const wrappedClient = async (): Promise<WrappedSplitClient> => {
    const client = await splitClient;
    return {
      getTreatment: (experiment: string, attr: SplitIO.Attributes = {}) =>
        client?.getTreatment(experiment, { ...attributes, ...attr }),
      getTreatmentWithConfig: (
        experiment: string,
        attr: SplitIO.Attributes = {},
      ) =>
        client?.getTreatmentWithConfig(experiment, { ...attributes, ...attr }),
    };
  };
  return {
    client: wrappedClient(),
    updateUserId,
    updateAttributes,
  };
};

function useTreatmentWithConfig<TConfig>(flagName: string): {
  treatment: string;
  config: Partial<TConfig>;
} {
  const [treatment, setTreatment] = useState<string>('control');
  const [config, setConfig] = useState<Partial<TConfig>>({});

  const { attributes, client: clientPromise } = useContext(context);

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;
      const { treatment: treatmentValue, config: treatmentConfig } =
        client?.getTreatmentWithConfig(flagName, attributes) ?? {};

      setTreatment(treatmentValue ?? 'control');
      setConfig(
        treatmentConfig ? (JSON.parse(treatmentConfig) as TConfig) : {},
      );
    })();
  }, [attributes, clientPromise, flagName]);

  return { treatment, config };
}

const useTreatment = (flagName: string) => {
  const [treatment, setTreatment] = useState<string | undefined>();

  const { attributes, client: clientPromise } = useContext(context);

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;

      setTreatment(client?.getTreatment(flagName, { ...attributes }));
    })();
  }, [attributes, clientPromise, flagName]);

  return treatment;
};

export {
  SplitContext,
  Provider,
  defaultValue,
  useSplit,
  useTreatment,
  useTreatmentWithConfig,
};
